import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Quote } from '@shared/types/index.ts';
import { offlinePrivacyResetIsCurrent } from './offline-privacy-reset.ts';

/**
 * Local draft storage for offline and flaky-connection editing (spec §17).
 *
 * What is stored is the working copy of a quote the user is actively editing,
 * plus the version it was based on. That is enough to (a) survive a reload or a
 * dropped connection and (b) detect on reconnect whether the server moved on
 * independently.
 *
 * Nothing is stored that the browser does not already hold in memory to render
 * the editor, and drafts are cleared on sign-out.
 */

const DB_NAME = 'web-app';
const DB_VERSION = 1;
const STORE = 'quote-drafts';
/**
 * New multi-owner rows use a physical key an old app will never request.
 * Keeping the sidecar in the existing store means old sign-out code still
 * privacy-wipes it, while an old debounce at the logical quote id cannot
 * overwrite another tab's variants.
 */
const BUNDLE_KEY_PREFIX = '\u0000cqb-offline-drafts-v2:';
const BUNDLE_INDEX_SENTINEL = Number.MAX_SAFE_INTEGER;

/**
 * A quote snapshot is useful offline, but it is still customer data. Keep the
 * cache intentionally small enough that opening the editor never turns into a
 * scan of somebody's whole account (or a browser-quota surprise).
 *
 * Clean snapshots are only a cold-start cache and are freely evictable. Dirty
 * work is different: it is never silently deleted to make room. When protected
 * storage is full, a new dirty write is refused and the editor tells the user
 * to reconnect instead of claiming work is safe when it is not.
 */
export const DRAFT_RETENTION = {
  maxEntries: 20,
  maxCleanEntries: 10,
  maxEntryBytes: 1024 * 1024,
  maxTotalBytes: 8 * 1024 * 1024,
  /** Avoid rewriting a multi-megabyte row on every local debounce. */
  maxVariantsPerQuote: 4,
  maxBundleBytes: 2 * 1024 * 1024,
  maxAcknowledgedOwners: 64,
  maxCleanAgeMs: 7 * 24 * 60 * 60 * 1000,
} as const;

export interface QuoteDraft {
  quoteId: string;
  /**
   * Authorization boundary that was active when this row was written.
   * Missing legacy rows cannot be attributed safely and are never restored.
   */
  storageScope?: string;
  /** Server version this draft was based on. */
  baseVersion: number;
  quote: Quote;
  /** Missing on records written by older releases; those are treated as dirty. */
  isDirty: boolean;
  /**
   * Identifies the editor activation that produced a dirty row. The revision is
   * monotonic only within that owner.
   */
  editOwnerId?: string;
  editRevision?: number;
  /** Dirty revisions known to be acknowledged; carried through successors as CAS history. */
  acknowledgedEdits?: DraftAcknowledgment[];
  /** Once the bounded owner journal is full, unknown owners fail closed. */
  ownerAdmissionClosed?: boolean;
  updatedAt: number;
  lastSyncedAt: number | null;
  /** UTF-8 JSON bytes, used only to enforce the local cache budget. */
  sizeBytes: number;
}

interface QuoteDraftBundle {
  /** Reserved physical IndexedDB key, never a logical quote id. */
  quoteId: string;
  logicalQuoteId: string;
  variants: QuoteDraft[];
  /**
   * Old clients may scan the shared by-updated index. A non-stale sentinel
   * keeps them from pruning a wrapper they cannot understand; variant times
   * remain authoritative for all new-client retention decisions.
   */
  updatedAt: number;
  sizeBytes: number;
}

type StoredQuoteDraft = QuoteDraft | QuoteDraftBundle;

interface DraftSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: StoredQuoteDraft;
    indexes: { 'by-updated': number };
  };
}

let dbPromise: Promise<IDBPDatabase<DraftSchema>> | null = null;

/**
 * Set while signing out.
 *
 * Unmounting the editor flushes whatever is still inside its debounce, and that
 * flush can land after the wipe. Rather than racing the ordering, writes are
 * refused outright from the moment sign-out begins.
 */
let writesSuspended = false;
let activeStorageScope: string | null = null;
let storageGeneration = 0;
/** Changes only at sign-out/impersonation, invalidating explicit old-scope flushes. */
let writeFenceGeneration = 0;

/**
 * Installs the user/company/location/authorization boundary for quote rows.
 * A generation fence keeps an old tab's in-flight IndexedDB work from being
 * published after a permission or location change.
 */
export function setDraftScope(scope: string | null): void {
  if (activeStorageScope === scope) return;
  activeStorageScope = scope;
  storageGeneration += 1;
  if (scope === null) writeFenceGeneration += 1;
}

export function suspendDraftWrites(): void {
  writesSuspended = true;
  storageGeneration += 1;
  writeFenceGeneration += 1;
}

export function resumeDraftWrites(): void {
  writesSuspended = false;
}

function db(): Promise<IDBPDatabase<DraftSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<DraftSchema>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'quoteId' });
        store.createIndex('by-updated', 'updatedAt');
      },
    });
  }
  return dbPromise;
}

/** IndexedDB is unavailable in private modes and some embedded browsers.
 *  Draft storage is an enhancement, so every operation fails soft. */
async function safely<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

/** Old records predate the flag and represent unsaved work, so fail safe. */
export function draftIsDirty(draft: QuoteDraft): boolean {
  return draft.isDirty !== false;
}

/** A row without an exact authorization scope is never safe to restore. */
export function draftBelongsToScope(draft: QuoteDraft, scope: string | null): boolean {
  return Boolean(scope) && draft.storageScope === scope;
}

function measuredBytes(draft: Omit<QuoteDraft, 'sizeBytes'> | QuoteDraft): number {
  // TextEncoder measures the JSON IndexedDB has to retain much more closely
  // than string.length (which under-counts non-ASCII customer names/notes).
  // Leave a small allowance for the size field itself and structured-clone
  // metadata so the stated ceiling is conservative rather than optimistic.
  return new TextEncoder().encode(JSON.stringify({ ...draft, sizeBytes: undefined })).byteLength + 64;
}

function normalizedDraft(draft: QuoteDraft): QuoteDraft {
  const normalized = { ...draft, isDirty: draftIsDirty(draft) };
  return { ...normalized, sizeBytes: measuredBytes(normalized) };
}

function bundleKey(quoteId: string): string {
  return BUNDLE_KEY_PREFIX + quoteId;
}

function isDraftBundle(row: StoredQuoteDraft): row is QuoteDraftBundle {
  return (
    'variants' in row &&
    Array.isArray(row.variants) &&
    typeof (row as QuoteDraftBundle).logicalQuoteId === 'string' &&
    row.quoteId === bundleKey((row as QuoteDraftBundle).logicalQuoteId)
  );
}

function isPlainDraftRow(row: StoredQuoteDraft): row is QuoteDraft {
  return (
    !isDraftBundle(row) &&
    !row.quoteId.startsWith(BUNDLE_KEY_PREFIX) &&
    typeof (row as QuoteDraft).baseVersion === 'number' &&
    Boolean((row as QuoteDraft).quote)
  );
}

function draftsInRow(row: StoredQuoteDraft | undefined): QuoteDraft[] {
  if (!row) return [];
  return (isDraftBundle(row) ? row.variants : [row]).map(normalizedDraft);
}

function measuredBundleBytes(value: Omit<QuoteDraftBundle, 'sizeBytes'> | QuoteDraftBundle): number {
  return new TextEncoder().encode(JSON.stringify({ ...value, sizeBytes: undefined })).byteLength + 64;
}

function rowForDrafts(quoteId: string, drafts: readonly QuoteDraft[]): QuoteDraftBundle | null {
  const variants = drafts.map(normalizedDraft);
  if (variants.length === 0) return null;
  const withoutSize = {
    quoteId: bundleKey(quoteId),
    logicalQuoteId: quoteId,
    variants,
    updatedAt: BUNDLE_INDEX_SENTINEL,
  };
  return { ...withoutSize, sizeBytes: measuredBundleBytes(withoutSize) };
}

function newestVariantForSlot(drafts: readonly QuoteDraft[]): QuoteDraft | null {
  return (
    [...drafts].sort((left, right) => {
      if (draftIsDirty(left) && draftIsDirty(right)) {
        return (
          (right.editRevision ?? -1) - (left.editRevision ?? -1) ||
          right.baseVersion - left.baseVersion ||
          right.updatedAt - left.updatedAt
        );
      }
      return right.baseVersion - left.baseVersion || right.updatedAt - left.updatedAt;
    })[0] ?? null
  );
}

function deduplicatedVariants(drafts: readonly QuoteDraft[]): QuoteDraft[] {
  const bySlot = new Map<string, QuoteDraft[]>();
  for (const draft of drafts) {
    const slot = variantSlot(draft);
    const copies = bySlot.get(slot) ?? [];
    copies.push(draft);
    bySlot.set(slot, copies);
  }
  return [...bySlot.values()].map((copies) => {
    const newest = newestVariantForSlot(copies)!;
    const acknowledgedEdits = mergedAcknowledgments(...copies.map((copy) => copy.acknowledgedEdits));
    return normalizedDraft({
      ...newest,
      ...(acknowledgedEdits.length > 0 ? { acknowledgedEdits } : {}),
      ...(copies.some((copy) => copy.ownerAdmissionClosed) ? { ownerAdmissionClosed: true } : {}),
    });
  });
}

function newestDraft(drafts: readonly QuoteDraft[]): QuoteDraft | null {
  return (
    [...drafts].sort(
      (left, right) =>
        Number(draftIsDirty(right)) - Number(draftIsDirty(left)) ||
        right.updatedAt - left.updatedAt ||
        (right.editOwnerId ?? '').localeCompare(left.editOwnerId ?? '') ||
        right.baseVersion - left.baseVersion,
    )[0] ?? null
  );
}

interface MeasuredDraft {
  draft: QuoteDraft;
  bytes: number;
}

/**
 * Pure retention policy, exported so the hard bounds can be regression-tested
 * without depending on whether a test browser happens to provide IndexedDB.
 */
export function draftRetentionVictims(
  drafts: readonly QuoteDraft[],
  now = Date.now(),
  maxAgeOverrideMs?: number,
): Set<string> {
  const measured: MeasuredDraft[] = drafts.map((draft) => {
    const normalized = normalizedDraft(draft);
    return { draft: normalized, bytes: normalized.sizeBytes };
  });
  const victims = new Set<string>();

  for (const entry of measured) {
    const ageLimit = maxAgeOverrideMs ?? DRAFT_RETENTION.maxCleanAgeMs;
    // Unsynced work is never an automatic retention victim. The write path
    // refuses a dirty record that cannot fit, leaving its previous durable
    // version untouched and surfacing the capacity problem in the editor.
    if (
      !draftIsDirty(entry.draft) &&
      (entry.bytes > DRAFT_RETENTION.maxEntryBytes || entry.draft.updatedAt < now - ageLimit)
    ) {
      victims.add(entry.draft.quoteId);
    }
  }

  let kept = measured.filter((entry) => !victims.has(entry.draft.quoteId));
  const cleanOldestFirst = kept
    .filter((entry) => !draftIsDirty(entry.draft))
    .sort((left, right) => left.draft.updatedAt - right.draft.updatedAt);

  while (cleanOldestFirst.length > DRAFT_RETENTION.maxCleanEntries) {
    const oldest = cleanOldestFirst.shift();
    if (oldest) victims.add(oldest.draft.quoteId);
  }

  kept = kept.filter((entry) => !victims.has(entry.draft.quoteId));
  let keptCount = kept.length;
  let keptBytes = kept.reduce((total, entry) => total + entry.bytes, 0);

  // Only acknowledged snapshots may be sacrificed for global pressure.
  // `draftBudgetStatus` detects when those are insufficient and refuses the
  // candidate rather than deleting somebody's unsynced work.
  const evictionOrder = kept
    .filter((entry) => !draftIsDirty(entry.draft))
    .sort((left, right) => left.draft.updatedAt - right.draft.updatedAt);

  for (const entry of evictionOrder) {
    if (keptCount <= DRAFT_RETENTION.maxEntries && keptBytes <= DRAFT_RETENTION.maxTotalBytes) break;
    victims.add(entry.draft.quoteId);
    keptCount -= 1;
    keptBytes -= entry.bytes;
  }

  return victims;
}

export type DraftToSave = Omit<QuoteDraft, 'updatedAt' | 'sizeBytes' | 'acknowledgedEdits' | 'ownerAdmissionClosed'>;

export interface DraftAcknowledgment {
  editOwnerId: string;
  throughRevision: number;
}

export interface DraftSaveOptions {
  /**
   * A clean server response may retire only dirty writes the request actually
   * included. Multiple owners cover a recovered row plus edits made after it
   * was reopened under a new activation.
   */
  acknowledgments?: readonly DraftAcknowledgment[];
  /** Conservative compatibility for dirty rows written before owner metadata. */
  legacyAcknowledgment?: Pick<DraftToSave, 'baseVersion' | 'quote'>;
}

export type DraftSaveResult = 'saved' | 'inactive' | 'entry-too-large' | 'capacity-reached';

function mergedAcknowledgments(...groups: Array<readonly DraftAcknowledgment[] | undefined>): DraftAcknowledgment[] {
  const revisions = new Map<string, number>();
  for (const group of groups) {
    for (const acknowledgment of group ?? []) {
      const previous = revisions.get(acknowledgment.editOwnerId);
      if (previous === undefined && revisions.size >= DRAFT_RETENTION.maxAcknowledgedOwners) continue;
      revisions.set(acknowledgment.editOwnerId, Math.max(previous ?? -1, acknowledgment.throughRevision));
    }
  }
  return [...revisions].map(([editOwnerId, throughRevision]) => ({ editOwnerId, throughRevision }));
}

function acknowledgmentCoversDraft(draft: QuoteDraft, options?: DraftSaveOptions): boolean {
  if (draft.editOwnerId && Number.isSafeInteger(draft.editRevision)) {
    return Boolean(
      options?.acknowledgments?.some(
        (acknowledgment) =>
          acknowledgment.editOwnerId === draft.editOwnerId &&
          (draft.editRevision as number) <= acknowledgment.throughRevision,
      ),
    );
  }
  const legacy = options?.legacyAcknowledgment;
  return Boolean(
    legacy && draft.baseVersion === legacy.baseVersion && JSON.stringify(draft.quote) === JSON.stringify(legacy.quote),
  );
}

/**
 * Whether a candidate can be retained after evicting clean snapshots only.
 * Exported so the no-data-loss boundary can be tested without IndexedDB.
 */
export function draftBudgetStatus(
  existing: readonly QuoteDraft[],
  candidate: QuoteDraft,
): Exclude<DraftSaveResult, 'inactive'> {
  const normalized = normalizedDraft(candidate);
  if (normalized.sizeBytes > DRAFT_RETENTION.maxEntryBytes) return 'entry-too-large';

  const prospective = [...existing.filter((entry) => entry.quoteId !== normalized.quoteId), normalized];
  const victims = draftRetentionVictims(prospective, normalized.updatedAt);
  if (victims.has(normalized.quoteId)) return 'capacity-reached';

  const kept = prospective.filter((entry) => !victims.has(entry.quoteId)).map(normalizedDraft);
  const bytes = kept.reduce((total, entry) => total + entry.sizeBytes, 0);
  return kept.length > DRAFT_RETENTION.maxEntries || bytes > DRAFT_RETENTION.maxTotalBytes
    ? 'capacity-reached'
    : 'saved';
}

function variantSlot(draft: QuoteDraft): string {
  return JSON.stringify([
    draft.storageScope ?? null,
    draftIsDirty(draft) ? 'dirty' : 'clean',
    draftIsDirty(draft) ? (draft.editOwnerId ?? 'legacy') : null,
  ]);
}

function retentionVictimIndexes(
  drafts: readonly QuoteDraft[],
  now = Date.now(),
  maxAgeOverrideMs?: number,
): Set<number> {
  const policyCopies = drafts.map((draft, index) => ({ ...draft, quoteId: String(index) }));
  return new Set(
    [...draftRetentionVictims(policyCopies, now, maxAgeOverrideMs)].map((value) => Number.parseInt(value, 10)),
  );
}

function bundleFits(drafts: readonly QuoteDraft[]): boolean {
  const row = drafts.length > 0 ? rowForDrafts(drafts[0]!.quoteId, drafts) : null;
  return (
    drafts.length <= DRAFT_RETENTION.maxVariantsPerQuote &&
    drafts.every((draft) => normalizedDraft(draft).sizeBytes <= DRAFT_RETENTION.maxEntryBytes) &&
    (!row || !isDraftBundle(row) || row.sizeBytes <= DRAFT_RETENTION.maxBundleBytes)
  );
}

export async function saveDraft(
  draft: DraftToSave,
  scopeOverride?: string,
  options?: DraftSaveOptions,
): Promise<DraftSaveResult> {
  const storageScope = scopeOverride ?? activeStorageScope;
  const generation = storageGeneration;
  const writeGeneration = writeFenceGeneration;
  if (writesSuspended || !storageScope || !offlinePrivacyResetIsCurrent()) return 'inactive';
  return safely(async () => {
    const database = await db();
    if (
      writesSuspended ||
      !activeStorageScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
    ) {
      return 'inactive' as const;
    }

    const updatedAt = Date.now();
    const withoutSize = {
      ...draft,
      storageScope,
      updatedAt,
      ...(options?.acknowledgments?.length
        ? { acknowledgedEdits: options.acknowledgments.map((acknowledgment) => ({ ...acknowledgment })) }
        : {}),
    };
    let record: QuoteDraft = normalizedDraft({ ...withoutSize, sizeBytes: measuredBytes(withoutSize) });
    const transaction = database.transaction(STORE, 'readwrite');
    const writeStillCurrent = () =>
      !writesSuspended &&
      Boolean(activeStorageScope) &&
      offlinePrivacyResetIsCurrent() &&
      writeGeneration === writeFenceGeneration &&
      Boolean(scopeOverride || (activeStorageScope === storageScope && storageGeneration === generation));
    const allRows = await transaction.store.getAll();
    const bundleRows = allRows.filter(isDraftBundle);
    const plainRows = allRows.filter(isPlainDraftRow);
    const existingRow = bundleRows.find((row) => row.logicalQuoteId === record.quoteId);
    const existingPlainRow = plainRows.find((row) => row.quoteId === record.quoteId);
    const attributablePlain = existingPlainRow?.storageScope ? normalizedDraft(existingPlainRow) : null;
    const allExistingCopies = [...draftsInRow(existingRow), ...(attributablePlain ? [attributablePlain] : [])];
    const existingCopies = deduplicatedVariants(allExistingCopies);
    const otherScopeCopies = existingCopies.filter((copy) => !draftBelongsToScope(copy, storageScope));
    const scopedCopies = existingCopies.filter((copy) => draftBelongsToScope(copy, storageScope));
    const allAcknowledgedOwners = new Set(
      [
        ...allExistingCopies
          .filter((copy) => draftBelongsToScope(copy, storageScope))
          .flatMap((copy) => copy.acknowledgedEdits ?? []),
        ...(record.acknowledgedEdits ?? []),
        ...(options?.acknowledgments ?? []),
      ].map((acknowledgment) => acknowledgment.editOwnerId),
    );
    const acknowledgmentHistory = mergedAcknowledgments(
      ...allExistingCopies
        .filter((copy) => draftBelongsToScope(copy, storageScope))
        .map((copy) => copy.acknowledgedEdits),
      record.acknowledgedEdits,
      options?.acknowledgments,
    );
    const ownerAdmissionClosed =
      scopedCopies.some((copy) => copy.ownerAdmissionClosed) ||
      allAcknowledgedOwners.size >= DRAFT_RETENTION.maxAcknowledgedOwners;
    if (acknowledgmentHistory.length > 0 || ownerAdmissionClosed) {
      record = normalizedDraft({
        ...record,
        ...(acknowledgmentHistory.length > 0 ? { acknowledgedEdits: acknowledgmentHistory } : {}),
        ...(ownerAdmissionClosed ? { ownerAdmissionClosed: true } : {}),
      });
    }

    const sameOwnerAlreadyExists = scopedCopies.some(
      (copy) => draftIsDirty(copy) && variantSlot(copy) === variantSlot(record),
    );
    const knownAcknowledgedOwner = Boolean(
      record.editOwnerId &&
      acknowledgmentHistory.some((acknowledgment) => acknowledgment.editOwnerId === record.editOwnerId),
    );
    if (draftIsDirty(record) && ownerAdmissionClosed && !sameOwnerAlreadyExists && !knownAcknowledgedOwner) {
      // Never discard a tombstone to admit owner 65. A genuinely new editor
      // receives a visible capacity failure; known owners and already-durable
      // variants can still finish.
      await transaction.done;
      return 'capacity-reached' as const;
    }

    if (draftIsDirty(record) && acknowledgmentCoversDraft(record, { acknowledgments: acknowledgmentHistory })) {
      // A debounce captured before its request completed. The row that carries
      // the ACK history is authoritative even when another owner's dirty copy
      // has since become the newest recovery.
      await transaction.done;
      return 'saved' as const;
    }

    let candidateCopy: QuoteDraft | null = null;
    let nextScopedCopies: QuoteDraft[];
    let acknowledgedDirtyWasRemoved = false;
    if (draftIsDirty(record)) {
      const unacknowledgedCopies = scopedCopies.filter((copy) => {
        if (!draftIsDirty(copy)) return true;
        const covered = acknowledgmentCoversDraft(copy, options);
        acknowledgedDirtyWasRemoved ||= covered;
        return !covered;
      });
      const slot = variantSlot(record);
      const sameOwner = unacknowledgedCopies.filter((copy) => variantSlot(copy) === slot);
      const newestSameOwner = newestVariantForSlot(sameOwner);
      const delayedSameOwner = Boolean(
        newestSameOwner &&
        (newestSameOwner.baseVersion > record.baseVersion ||
          (typeof newestSameOwner.editRevision === 'number' &&
            typeof record.editRevision === 'number' &&
            newestSameOwner.editRevision > record.editRevision)),
      );
      if (delayedSameOwner && !options?.acknowledgments?.length && !options?.legacyAcknowledgment) {
        await transaction.done;
        return 'saved' as const;
      }
      if (delayedSameOwner && newestSameOwner) {
        // The newer same-owner debounce is already the correct successor, but
        // this late callback still carries a real server ACK. Apply its covered
        // owner cleanup and tombstones around that durable successor instead of
        // returning early and letting an inherited variant replay later.
        nextScopedCopies = unacknowledgedCopies;
        candidateCopy = newestSameOwner;
      } else {
        nextScopedCopies = [...unacknowledgedCopies.filter((copy) => variantSlot(copy) !== slot), record];
        candidateCopy = record;
      }
    } else {
      const remainingDirty = scopedCopies.filter((copy) => {
        if (!draftIsDirty(copy)) return false;
        const covered = acknowledgmentCoversDraft(copy, options);
        acknowledgedDirtyWasRemoved ||= covered;
        return !covered;
      });
      const existingClean = newestDraft(scopedCopies.filter((copy) => !draftIsDirty(copy)));
      const canonical =
        existingClean && existingClean.baseVersion > record.baseVersion
          ? normalizedDraft({ ...existingClean, acknowledgedEdits: acknowledgmentHistory })
          : record;
      nextScopedCopies = [...remainingDirty, canonical];
      candidateCopy = canonical === record ? canonical : null;
    }

    // Carry the bounded owner CAS history through every successor. If the clean
    // cache row is later evicted, a surviving sibling still suppresses a
    // debounce whose contents the server already acknowledged.
    if (acknowledgmentHistory.length > 0) {
      nextScopedCopies = nextScopedCopies.map((copy) => {
        const enriched = normalizedDraft({
          ...copy,
          acknowledgedEdits: acknowledgmentHistory,
          ...(ownerAdmissionClosed ? { ownerAdmissionClosed: true } : {}),
        });
        if (copy === candidateCopy) candidateCopy = enriched;
        return enriched;
      });
    }

    let desiredCurrentCopies = [...otherScopeCopies, ...nextScopedCopies];
    const candidateTooLarge = (candidateCopy?.sizeBytes ?? record.sizeBytes) > DRAFT_RETENTION.maxEntryBytes;
    if (draftIsDirty(record) && !candidateTooLarge && !bundleFits(desiredCurrentCopies)) {
      // Per-quote bounds protect debounce performance, but clean cold-start
      // copies are explicitly expendable. Make room locally before refusing a
      // new dirty owner; never sacrifice another owner's unsynced variant.
      const cleanOldestFirst = desiredCurrentCopies
        .filter((copy) => !draftIsDirty(copy))
        .sort((left, right) => left.updatedAt - right.updatedAt || variantSlot(left).localeCompare(variantSlot(right)));
      while (!bundleFits(desiredCurrentCopies) && cleanOldestFirst.length > 0) {
        const victim = cleanOldestFirst.shift();
        desiredCurrentCopies = desiredCurrentCopies.filter((copy) => copy !== victim);
      }
    }
    const desiredBundleFits = bundleFits(desiredCurrentCopies);
    if (candidateTooLarge || !desiredBundleFits) {
      if (draftIsDirty(record)) {
        // The caller must be told its own owner-scoped copy is not durable. No
        // existing dirty owner is touched to make space.
        await transaction.done;
        return candidateTooLarge ? 'entry-too-large' : 'capacity-reached';
      }

      // A live server ACK is durable even when its optional clean cache cannot
      // fit. Retire only the variants that exact owner/revision CAS covered,
      // carry the tombstone history on surviving siblings, and leave every
      // unrelated dirty copy untouched.
      if (acknowledgedDirtyWasRemoved) {
        if (!writeStillCurrent()) {
          await transaction.done;
          return 'inactive' as const;
        }
        const fallbackCopies = [...otherScopeCopies, ...nextScopedCopies.filter((copy) => draftIsDirty(copy))];
        if (bundleFits(fallbackCopies)) {
          const fallback = rowForDrafts(record.quoteId, fallbackCopies);
          const writes: Array<Promise<unknown>> = [];
          if (fallback) writes.push(transaction.store.put(fallback));
          else writes.push(transaction.store.delete(bundleKey(record.quoteId)));
          if (existingPlainRow) writes.push(transaction.store.delete(existingPlainRow.quoteId));
          await Promise.all(writes);
        } else if (attributablePlain && acknowledgmentCoversDraft(attributablePlain, options)) {
          // The sidecar must remain byte-for-byte unchanged when enriched
          // siblings cannot fit, but an exact legacy/plain ACK is independently
          // safe to retire in this same serialized transaction.
          await transaction.store.delete(existingPlainRow!.quoteId);
        }
      }
      await transaction.done;
      return candidateTooLarge ? 'entry-too-large' : 'capacity-reached';
    }

    const states = new Map<string, QuoteDraft[]>();
    for (const row of bundleRows) states.set(row.logicalQuoteId, draftsInRow(row));
    states.set(record.quoteId, desiredCurrentCopies);

    const bundledEntries = [...states].flatMap(([quoteId, copies]) =>
      copies.map((copy) => ({
        quoteId,
        copy,
        physicalKey: bundleKey(quoteId),
        source: 'bundle' as const,
        isCandidate: copy === candidateCopy,
      })),
    );
    const externalPlainEntries = plainRows
      .filter(
        (row) => row !== existingPlainRow && Boolean(row.storageScope) && !row.quoteId.startsWith(BUNDLE_KEY_PREFIX),
      )
      .map((row) => ({
        quoteId: row.quoteId,
        copy: normalizedDraft(row),
        physicalKey: row.quoteId,
        source: 'plain' as const,
        isCandidate: false,
      }));
    const flattened = [...bundledEntries, ...externalPlainEntries];
    const victimIndexes = retentionVictimIndexes(
      flattened.map((entry) => entry.copy),
      updatedAt,
    );
    const kept = flattened.filter((_, index) => !victimIndexes.has(index));
    const keptBytes = kept.reduce((total, entry) => total + normalizedDraft(entry.copy).sizeBytes, 0);
    const overProtectedCapacity = kept.length > DRAFT_RETENTION.maxEntries || keptBytes > DRAFT_RETENTION.maxTotalBytes;
    const candidateWasEvicted = flattened.some((entry, index) => entry.isCandidate && victimIndexes.has(index));

    if (overProtectedCapacity || (draftIsDirty(record) && candidateWasEvicted)) {
      // Retention is allowed to sacrifice only clean cache copies. If that is
      // insufficient, the new dirty owner is refused and the transaction leaves
      // every existing recovery byte unchanged.
      await transaction.done;
      return 'capacity-reached';
    }

    const keptByQuote = new Map<string, QuoteDraft[]>();
    for (const entry of kept) {
      if (entry.source !== 'bundle') continue;
      const copies = keptByQuote.get(entry.quoteId) ?? [];
      copies.push(entry.copy);
      keptByQuote.set(entry.quoteId, copies);
    }
    if (!writeStillCurrent()) {
      await transaction.done;
      return 'inactive' as const;
    }
    const writes: Array<Promise<unknown>> = [...states.keys()].map((quoteId) => {
      const row = rowForDrafts(quoteId, keptByQuote.get(quoteId) ?? []);
      return row ? transaction.store.put(row) : transaction.store.delete(bundleKey(quoteId));
    });
    if (existingPlainRow) writes.push(transaction.store.delete(existingPlainRow.quoteId));
    flattened.forEach((entry, index) => {
      if (entry.source === 'plain' && victimIndexes.has(index)) {
        writes.push(transaction.store.delete(entry.physicalKey));
      }
    });
    for (const row of plainRows) {
      if (!row.storageScope) writes.push(transaction.store.delete(row.quoteId));
    }
    await Promise.all(writes);
    await transaction.done;
    return candidateWasEvicted ? 'capacity-reached' : 'saved';
  }, 'inactive');
}

export async function loadDraft(quoteId: string, scopeOverride?: string): Promise<QuoteDraft | null> {
  const storageScope = scopeOverride ?? activeStorageScope;
  const generation = storageGeneration;
  const writeGeneration = writeFenceGeneration;
  if (writesSuspended || !storageScope || !offlinePrivacyResetIsCurrent()) return null;
  return safely(async () => {
    const database = await db();
    if (
      writesSuspended ||
      !activeStorageScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
    ) {
      return null;
    }

    // Sidecar cleanup, safe legacy migration, and selection share one store
    // transaction. Old tabs write only the plain logical key; serializing both
    // keys here prevents either writer from erasing another owner's recovery.
    const transaction = database.transaction(STORE, 'readwrite');
    const [bundleCandidate, plainCandidate] = await Promise.all([
      transaction.store.get(bundleKey(quoteId)),
      transaction.store.get(quoteId),
    ]);
    const storedBundle =
      bundleCandidate && isDraftBundle(bundleCandidate) && bundleCandidate.logicalQuoteId === quoteId
        ? bundleCandidate
        : undefined;
    const storedPlain =
      plainCandidate && isPlainDraftRow(plainCandidate) && plainCandidate.quoteId === quoteId
        ? plainCandidate
        : undefined;
    if (!storedBundle && !storedPlain) {
      await transaction.done;
      return null;
    }

    let sidecarChanged = false;
    const retainedSidecar = draftsInRow(storedBundle).filter((copy) => {
      if (!copy.storageScope) {
        sidecarChanged = true;
        return false;
      }
      if (!draftBelongsToScope(copy, storageScope)) return true;
      const isVictim = retentionVictimIndexes([copy]).has(0);
      sidecarChanged ||= isVictim;
      return !isVictim;
    });

    let retainedPlain = storedPlain?.storageScope ? normalizedDraft(storedPlain) : null;
    let deletePlain = Boolean(storedPlain && !storedPlain.storageScope);
    if (
      retainedPlain &&
      draftBelongsToScope(retainedPlain, storageScope) &&
      retentionVictimIndexes([retainedPlain]).has(0)
    ) {
      retainedPlain = null;
      deletePlain = true;
    }

    if (retainedPlain && draftIsDirty(retainedPlain)) {
      const acknowledged = mergedAcknowledgments(...retainedSidecar.map((copy) => copy.acknowledgedEdits));
      if (
        acknowledgmentCoversDraft(retainedPlain, {
          acknowledgments: acknowledged,
        })
      ) {
        // An old tab can flush a debounce after a new tab's ACK. Never promote
        // that acknowledged plain row back into the protected sidecar.
        retainedPlain = null;
        deletePlain = true;
      }
    }

    const combined = deduplicatedVariants([...retainedSidecar, ...(retainedPlain ? [retainedPlain] : [])]);
    const migratePlain = Boolean(retainedPlain && bundleFits(combined));

    if (
      writesSuspended ||
      !activeStorageScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
    ) {
      await transaction.done;
      return null;
    }

    const writes: Array<Promise<unknown>> = [];
    if (migratePlain) {
      const row = rowForDrafts(quoteId, combined);
      if (row) writes.push(transaction.store.put(row));
      else writes.push(transaction.store.delete(bundleKey(quoteId)));
      writes.push(transaction.store.delete(storedPlain!.quoteId));
    } else if (sidecarChanged) {
      const row = rowForDrafts(quoteId, retainedSidecar);
      if (row) writes.push(transaction.store.put(row));
      else writes.push(transaction.store.delete(bundleKey(quoteId)));
    }
    if (deletePlain && storedPlain) writes.push(transaction.store.delete(storedPlain.quoteId));
    await Promise.all(writes);

    const selectable = combined.filter((copy) => draftBelongsToScope(copy, storageScope));
    const selected = newestDraft(selectable);
    await transaction.done;
    return writesSuspended ||
      !activeStorageScope ||
      !offlinePrivacyResetIsCurrent() ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
      ? null
      : selected;
  }, null);
}

/**
 * Ordinary invalidation removes only acknowledged cache copies. Dirty variants
 * require an explicit owner/revision acknowledgment and are otherwise kept.
 */
export async function clearDraft(
  quoteId: string,
  scopeOverride?: string,
  acknowledgments?: DraftSaveOptions,
): Promise<void> {
  const storageScope = scopeOverride ?? activeStorageScope;
  const generation = storageGeneration;
  const writeGeneration = writeFenceGeneration;
  if (!storageScope) return;
  await safely(async () => {
    const database = await db();
    if (
      !activeStorageScope ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
    ) {
      return;
    }
    const transaction = database.transaction(STORE, 'readwrite');
    const [bundleCandidate, plainCandidate] = await Promise.all([
      transaction.store.get(bundleKey(quoteId)),
      transaction.store.get(quoteId),
    ]);
    const storedBundle =
      bundleCandidate && isDraftBundle(bundleCandidate) && bundleCandidate.logicalQuoteId === quoteId
        ? bundleCandidate
        : undefined;
    const storedPlain =
      plainCandidate && isPlainDraftRow(plainCandidate) && plainCandidate.quoteId === quoteId
        ? plainCandidate
        : undefined;
    const retained = draftsInRow(storedBundle).filter((copy) => {
      if (!draftBelongsToScope(copy, storageScope)) return true;
      if (!draftIsDirty(copy)) return false;
      return !acknowledgmentCoversDraft(copy, acknowledgments);
    });
    const writes: Array<Promise<unknown>> = [];
    if (storedBundle) {
      const row = rowForDrafts(quoteId, retained);
      if (row) writes.push(transaction.store.put(row));
      else writes.push(transaction.store.delete(bundleKey(quoteId)));
    }
    if (
      storedPlain &&
      (!storedPlain.storageScope ||
        (draftBelongsToScope(storedPlain, storageScope) &&
          (!draftIsDirty(storedPlain) || acknowledgmentCoversDraft(storedPlain, acknowledgments))))
    ) {
      writes.push(transaction.store.delete(storedPlain.quoteId));
    }
    await Promise.all(writes);
    await transaction.done;
  }, undefined);
}

export async function clearAllDrafts(): Promise<boolean> {
  return safely(async () => {
    const database = await db();
    await database.clear(STORE);
    return true;
  }, false);
}

/** Drop clean snapshots in the active scope; dirty recovery rows stay put. */
export async function retireCleanQuoteDrafts(scopeOverride?: string): Promise<void> {
  const storageScope = scopeOverride ?? activeStorageScope;
  const generation = storageGeneration;
  const writeGeneration = writeFenceGeneration;
  if (!storageScope) return;
  await safely(async () => {
    const database = await db();
    if (
      !activeStorageScope ||
      writeGeneration !== writeFenceGeneration ||
      (!scopeOverride && (activeStorageScope !== storageScope || storageGeneration !== generation))
    ) {
      return;
    }
    const transaction = database.transaction(STORE, 'readwrite');
    const rows = await transaction.store.getAll();
    const writes: Array<Promise<unknown>> = [];
    for (const row of rows) {
      if (isDraftBundle(row)) {
        const retained = draftsInRow(row).filter(
          (copy) => !draftBelongsToScope(copy, storageScope) || draftIsDirty(copy),
        );
        const rebuilt = rowForDrafts(row.logicalQuoteId, retained);
        if (rebuilt) writes.push(transaction.store.put(rebuilt));
        else writes.push(transaction.store.delete(row.quoteId));
        continue;
      }
      if (isPlainDraftRow(row) && row.storageScope && draftBelongsToScope(row, storageScope) && !draftIsDirty(row)) {
        writes.push(transaction.store.delete(row.quoteId));
      }
    }
    await Promise.all(writes);
    await transaction.done;
  }, undefined);
}

interface DraftRetentionEntry {
  logicalQuoteId: string;
  physicalKey: string;
  copy: QuoteDraft;
  source: 'bundle' | 'plain';
}

/** Enforces age, count and byte bounds even when no quote is being saved. */
export async function pruneDrafts(maxAgeMs?: number): Promise<void> {
  await safely(async () => {
    const database = await db();
    const transaction = database.transaction(STORE, 'readwrite');
    const rows = await transaction.store.getAll();
    const entries = rows.flatMap<DraftRetentionEntry>((row) => {
      if (isDraftBundle(row)) {
        return draftsInRow(row).map((copy) => ({
          logicalQuoteId: row.logicalQuoteId,
          physicalKey: row.quoteId,
          copy,
          source: 'bundle' as const,
        }));
      }
      if (isPlainDraftRow(row) && row.storageScope) {
        return [
          {
            logicalQuoteId: row.quoteId,
            physicalKey: row.quoteId,
            copy: normalizedDraft(row),
            source: 'plain' as const,
          },
        ];
      }
      return [];
    });
    const victims = retentionVictimIndexes(
      entries.map((entry) => entry.copy),
      Date.now(),
      maxAgeMs,
    );
    const keptByQuote = new Map<string, QuoteDraft[]>();
    entries.forEach((entry, index) => {
      if (entry.source !== 'bundle' || victims.has(index)) return;
      const copies = keptByQuote.get(entry.logicalQuoteId) ?? [];
      copies.push(entry.copy);
      keptByQuote.set(entry.logicalQuoteId, copies);
    });
    const writes: Array<Promise<unknown>> = [];
    for (const row of rows) {
      if (isDraftBundle(row)) {
        const rebuilt = rowForDrafts(row.logicalQuoteId, keptByQuote.get(row.logicalQuoteId) ?? []);
        if (rebuilt) writes.push(transaction.store.put(rebuilt));
        else writes.push(transaction.store.delete(row.quoteId));
      } else if (isPlainDraftRow(row) && !row.storageScope) {
        // Default-branch rows predate authorization scoping and cannot be
        // restored safely or counted as protected work.
        writes.push(transaction.store.delete(row.quoteId));
      }
    }
    entries.forEach((entry, index) => {
      if (entry.source === 'plain' && victims.has(index)) {
        writes.push(transaction.store.delete(entry.physicalKey));
      }
    });
    await Promise.all(writes);
    await transaction.done;
  }, undefined);
}
